import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useFetcher, useOutletContext, useSearchParams } from '@remix-run/react';
import { ActionModalElement, type Tier as DreamTier, RetentionOptionElement } from '@shared/dream-components/src';
import classNames from 'classnames';
import type { TFunction } from 'i18next';

import useToast, { type ToastData } from '~/hooks/useToast';
import type { OutletContext } from '~/interfaces/outlet_context';
import { NECTAR_PAGE_BLOCK_GET_PARAM_NAME } from '~/utils/nectarTrackingFields';
import toast from '~/utils/toast';

import useStaticRetentionPremiumOffers from './hooks/useStaticRetentionPremiumOffers';

interface Props {
  isOpen: boolean;
  isDowngrading: boolean;
  isUnsubscribing: boolean;
  closeModal: () => void;
  onSuccess: () => void;
  mode: 'unsubscribe' | 'downgrade';
  isPremiumSubscription: boolean;
  showPause: boolean;
  areAllPlansGifts: boolean;
  areAllPlansDowngrading: boolean;
  tier?: DreamTier;
  subscriberId: string;
}

const getTranslatedPauseOptions = (t: TFunction) => {
  const options = [
    { label: t('shared.month.count.singular', { count: 1 }), value: '1' },
    { label: t('shared.month.count.plural', { count: 3 }), value: '3' },
    { label: t('shared.month.count.plural', { count: 6 }), value: '6' },
  ];

  return options;
};

export const RetainSubscriptionModal = ({
  isOpen,
  isDowngrading,
  isUnsubscribing,
  closeModal,
  onSuccess,
  mode,
  areAllPlansGifts,
  areAllPlansDowngrading,
  isPremiumSubscription,
  showPause,
  tier,
  subscriberId,
}: Props) => {
  const { t } = useTranslation();
  const { cookies } = useOutletContext<OutletContext>();
  const [searchParams] = useSearchParams();
  const { data: retentionPremiumOffers } = useStaticRetentionPremiumOffers({
    enabled: mode === 'downgrade' && isPremiumSubscription,
  });

  const fetcher = useFetcher<{
    toast?: ToastData;
  }>();
  const isLoading = fetcher.state === 'submitting' || fetcher.state === 'loading';

  const handleClickPausePlan = (duration?: string) => {
    if (!duration) {
      toast.error('Please select duration');
      return;
    }

    const formData = new FormData();
    formData.append('time_unit', 'months');
    formData.append('duration', duration);
    formData.append('subscriber_id', subscriberId);

    if (tier) {
      formData.append('tier_id', tier.id);
    }
    formData.append('pause_subscription_success_message', t('toast.success'));
    formData.append('pause_subscription_error_message', t('errors.default'));

    fetcher.submit(formData, {
      method: 'post',
      action: '/pause_subscription',
    });
  };

  const handleClickClaimOffer = (offerId: string) => {
    if (!offerId) {
      toast.error('Please select an offer');
      return;
    }

    if (!tier) {
      toast.error('Please select a tier');
      return;
    }

    const formData = new FormData();
    formData.append('offer_id', offerId);
    formData.append('tier_id', tier.id);
    formData.append('claim_subscription_retention_offer_success_message', t('toast.success'));
    formData.append('claim_subscription_retention_offer_error_message', t('errors.default'));

    fetcher.submit(formData, {
      method: 'post',
      action: '/claim_subscription_retention_offer',
    });
  };

  const handleClickSwitchToFree = () => {
    const formData = new FormData();
    if (tier) {
      formData.append('tier_id', tier.id);
    }
    formData.append('subscriber_id', subscriberId);
    if (cookies.last_resource_guid) {
      formData.append('last_resource_guid', cookies.last_resource_guid);
    }
    const nectarPageBlock = searchParams.get(NECTAR_PAGE_BLOCK_GET_PARAM_NAME);
    if (nectarPageBlock) {
      formData.append('nectar_page_block', nectarPageBlock);
    }
    formData.append('downgrade_success_message', t('account_actions.downgrade.toast.cancelled'));
    formData.append('downgrade_error_message', t('account_actions.downgrade.toast.error'));

    fetcher.submit(formData, {
      method: 'post',
      action: '/downgrade',
    });
  };

  useToast(fetcher?.data?.toast, onSuccess);

  const showDowngrade =
    isPremiumSubscription && Array.isArray(retentionPremiumOffers) && retentionPremiumOffers.length > 0;
  const showSwitchToFree = mode === 'unsubscribe' && isPremiumSubscription && !areAllPlansDowngrading;
  const isSingleOption = [showPause, showDowngrade, showSwitchToFree].filter(Boolean).length === 1;
  const isPausingOnePlan = Boolean(tier);
  const pauseBenefits =
    areAllPlansDowngrading || !isPremiumSubscription
      ? (t('retention_modal.pause_plan.benefits_while_downgrading', { returnObjects: true }) as string[])
      : [
          ...(t('retention_modal.pause_plan.benefits', { returnObjects: true }) as string[]),
          ...(isPausingOnePlan ? [t('retention_modal.pause_plan.keeps_free_emails')] : []),
        ];

  const getUnsubscribeButtonLabel = useCallback(() => {
    if (areAllPlansDowngrading || !isPremiumSubscription) {
      return t('retention_modal.unsubscribe_while_downgrading.button_label');
    }

    if (areAllPlansGifts) {
      return t('retention_modal.unsubscribe_while_gift.button_label');
    }

    return t('retention_modal.unsubscribe.button_label');
  }, [areAllPlansDowngrading, areAllPlansGifts, isPremiumSubscription, t]);

  const visibleRetentionOffers = useMemo(() => {
    if (!Array.isArray(retentionPremiumOffers) || !tier?.id) {
      return [];
    }

    return retentionPremiumOffers.filter((offer) => offer.tier_id === tier.id);
  }, [retentionPremiumOffers, tier?.id]);

  return (
    <ActionModalElement
      element={{
        type: 'actionModal',
      }}
      title={t('retention_modal.title')}
      message={
        mode === 'unsubscribe' ? t('retention_modal.unsubscribe.message') : t('retention_modal.downgrade.message')
      }
      submitLabel={
        mode === 'unsubscribe' ? (
          getUnsubscribeButtonLabel()
        ) : (
          <Trans
            i18nKey="retention_modal.downgrade.button_label"
            values={{ tier_name: tier?.name }}
            components={{ strong: <strong className="font-semibold" /> }}
          />
        )
      }
      isLoading={isLoading || isDowngrading || isUnsubscribing}
      isOpen={isOpen}
      onCancel={closeModal}
      onOpenChange={(_o: boolean) => closeModal()}
    >
      <div
        className={classNames(
          'flex flex-row gap-4 overflow-x-scroll sm:justify-center',
          isSingleOption && 'justify-center',
        )}
      >
        {showPause && (
          <RetentionOptionElement
            shouldUseThemeColors={isSingleOption}
            isLive
            element={{
              type: 'retentionOption',
            }}
            title={t('retention_modal.pause_plan.title')}
            reasons={pauseBenefits}
            buttonType={isSingleOption ? 'primary' : 'default'}
            buttonLabel={t('retention_modal.pause_plan.button_label')}
            isButtonDisabled={isLoading || isDowngrading || isUnsubscribing}
            onClick={(duration?: string) => handleClickPausePlan(duration)}
            pauseOptions={getTranslatedPauseOptions(t)}
          >
            {null}
          </RetentionOptionElement>
        )}
        {showDowngrade &&
          visibleRetentionOffers.map((offer) => (
            <RetentionOptionElement
              key={offer.id}
              shouldUseThemeColors
              isLive
              element={{
                type: 'retentionOption',
              }}
              title={offer.name}
              message={offer.terms}
              reasons={(() => {
                const benefits = t('retention_modal.downgrade_offer.benefits', { returnObjects: true }) as string[];
                return [
                  benefits[0],
                  ...(offer.discounted_benefit ? [offer.discounted_benefit] : []),
                  ...benefits.slice(1),
                ];
              })()}
              buttonType="primary"
              buttonLabel={t('retention_modal.downgrade_offer.button_label')}
              isButtonDisabled={isLoading || isDowngrading || isUnsubscribing}
              onClick={() => handleClickClaimOffer(offer.id)}
            >
              {null}
            </RetentionOptionElement>
          ))}
        {showSwitchToFree && (
          <RetentionOptionElement
            shouldUseThemeColors={isSingleOption}
            isLive
            element={{
              type: 'retentionOption',
            }}
            title={t('retention_modal.switch_to_free.title')}
            message={t('retention_modal.switch_to_free.message')}
            reasons={t('retention_modal.switch_to_free.benefits', { returnObjects: true }) as string[]}
            buttonType={isSingleOption ? 'primary' : 'default'}
            buttonLabel={t('retention_modal.switch_to_free.button_label')}
            isButtonDisabled={isLoading || isDowngrading}
            onClick={() => handleClickSwitchToFree()}
          >
            {null}
          </RetentionOptionElement>
        )}
      </div>
    </ActionModalElement>
  );
};
